import Vue from 'vue'
import router from '@/modules/router'
import { Order, Stripe, Restaurant } from '@/api'
const VueScrollTo = require('vue-scrollto')
import { EventBus } from '@/modules/plugins/modules/event'
import { markPurchasePending } from '@/modules/analytics/purchase'
import { TIER_TIME_OUTDATED, TIER_TIME_UNAVAILABLE } from '@/modules/helpers/deliveryPeriodSlots'

const state = {
    stripe: null,
    restaurant: null,
    loading: false,
    stillLoading: false,
    addressChange: false,
    validInvoiceInfo: true,
    orderErrors: [],
    calculationErrors: [],
    localOrderValidation: ['billingName', 'billingFullAddress', 'billingSubAddress'],
}

const mutations = {
    INIT(state) {
        if (!state.stripe) state.stripe = Vue.observable(new Stripe())
        if (!state.restairant) state.restaurant = Vue.observable(new Restaurant())
    },
    SET_LOADING(state, payload) {
        state.loading = payload
    },
    SET_STILL_LOADING(state, payload) {
        state.stillLoading = payload
    },
    SET_ORDER_ERRORS(state, error) {
        if (error?.violations) {
            state.orderErrors = []
            error.violations.forEach((violation) => {
                if (!state.localOrderValidation.includes(violation.propertyPath))
                    state.orderErrors.push(violation)
            })
        }
    },
    PUSH_ORDER_ERROR(state, error) {
        state.orderErrors.push(error)
    },
    RESET_ORDER_ERRORS(state) {
        state.orderErrors = []
    },
    SET_CALCULATION_ERRORS(state, errors) {
        state.calculationErrors = errors
    },
    SET_VALID_INFO_VALIDITY(state, payload) {
        state.validInvoiceInfo = payload
    },
    SET_ADDRESS_CHANGE(state, payload) {
        state.addressChange = payload
    },
}

const actions = {
    async init({ commit }) {
        try {
            await commit('INIT')
            if (Vue.prototype.$user?.isLoggedIn) await state.stripe.init()
            return Promise.resolve()
        } catch {
            return Promise.reject()
        }
    },
    async stripeInitAfterLogin({ state }) {
        if (Vue.prototype.$user?.isLoggedIn) {
            await state.stripe.init()
            await state.stripe.initOnlinePaymnet()
        }
    },
    setRestaurant({ commit }, payload) {
        commit('SET_RESTAURANT', payload)
    },
    setAddressChange({ commit }, payload) {
        commit('SET_ADDRESS_CHANGE', payload)
    },
    async placeOrder({ getters, commit, dispatch, rootGetters }, $event) {
        if (getters['orderDisabled']) {
            let firstError = getters['possibleErrors'].find(
                (error) => typeof error === 'string' && !!document.querySelector(`#${error}`)
            )
            if (!firstError) {
                firstError = getters['calculationErrors']
                    .map((error) => error.propertyPath)
                    .find((error) => {
                        return !!document.querySelector(`#${error}`)
                    })
            }
            const element = document.querySelector(`#${firstError}`)
            if (element) {
                VueScrollTo.scrollTo(`#${firstError}`)
                element.classList.add('highlight')
                setTimeout(() => {
                    element.classList.remove('highlight')
                }, 2000)
            }
            if (!firstError && !element && (getters['loading'] || Vue.prototype.$cart.loading)) {
                commit('SET_STILL_LOADING', true)
                setTimeout(() => {
                    commit('SET_STILL_LOADING', false)
                }, 2000)
            }
            return Promise.reject(
                getters['loading']
                    ? 'Something is still loading! Try again!'
                    : 'Order disabled, follow the information alerts!'
            )
        }
        if (
            Vue.prototype.$cart.isResidentialCanteen &&
            !rootGetters['active/canteen']?.address?.user_address
        ) {
            EventBus.$emit('openCanteenAddressModal', true)
            return Promise.reject('Save the residential canteen address first!')
        } else {
            if (!getters['calculationErrors']?.length) commit('RESET_ORDER_ERRORS')
            commit('SET_LOADING', true)
            try {
                await Vue.prototype.$cart.calculate(undefined, true)
                if (!getters['calculationErrors']?.length) {
                    const order = new Order()
                    try {
                        const orderResult = await order.placeOrder({
                            ...Vue.prototype.$cart.model,
                        })
                        markPurchasePending(orderResult?.id)
                        if (orderResult.redirect_url) {
                            commit('SET_LOADING', false)
                            window.location.href = orderResult.redirect_url
                        } else if (
                            orderResult.client_secret &&
                            orderResult.client_type === Vue.prototype.$cardPayment
                        ) {
                            if (getters['stripe']?.selectedCard)
                                await dispatch('stripePay', orderResult)
                            else
                                await dispatch('stripeOnlinePay', {
                                    order: orderResult,
                                    $event: $event,
                                })
                            return Promise.resolve(orderResult)
                        } else {
                            dispatch('goToSummary', {
                                orderId: orderResult.id,
                            })
                        }
                        return Promise.resolve(orderResult)
                    } catch (e) {
                        commit('SET_LOADING', false)
                        commit('SET_ORDER_ERRORS', e)
                        return Promise.reject(e)
                    }
                } else {
                    commit('SET_LOADING', false)
                    return Promise.reject(getters['calculationErrors'])
                }
            } catch (e) {
                commit('SET_LOADING', false)
                return Promise.reject(e)
            }
        }
    },
    goToSummary({ commit }, { orderId, error }) {
        if (!orderId) return Promise.reject('Missing ID to navigate to!')
        Vue.nextTick(() => {
            router.push(
                {
                    name: 'summary-page',
                    params: {
                        id: orderId,
                        error: error,
                    },
                },
                async () => {
                    const refreshExpenseRules = !!Vue.prototype.$cart.selected_expense_rule
                    await Vue.prototype.$cart.resetCart()
                    Vue.prototype.$user.getExpenseRules(refreshExpenseRules)
                    commit('SET_LOADING', false)
                }
            )
        })
    },
    async stripePay({ dispatch, commit, state }, order) {
        try {
            commit('SET_LOADING', true)
            const result = await state.stripe.payViaCard(
                order.client_secret,
                Vue.prototype.$cart.delivery_name
            )
            dispatch('goToSummary', {
                orderId: order.id,
                error: result?.paymentIntent?.error,
            })
            return Promise.resolve()
        } catch (e) {
            commit('PUSH_ORDER_ERROR', e)
            commit('SET_LOADING', false)
            return Promise.reject(e)
        }
    },
    async stripeOnlinePay({ dispatch, commit, state }, { order, $event }) {
        if (!$event) return Promise.reject('Missing stripe event.')
        try {
            commit('SET_LOADING', true)
            const result = await state.stripe.payOnline($event, order.client_secret)
            dispatch('goToSummary', {
                orderId: order.id,
                error: result?.paymentIntent?.last_payment_error,
            })
            return Promise.resolve()
        } catch (e) {
            commit('PUSH_ORDER_ERROR', e)
            commit('SET_LOADING', false)
            return Promise.reject(e)
        }
    },
    setCalculationErrors({ commit }, errors) {
        commit('SET_CALCULATION_ERRORS', errors)
    },
    setInvoiceInfoValidity({ commit }, payload) {
        commit('SET_VALID_INFO_VALIDITY', payload)
    },
}

const getters = {
    stripe: (state) => state.stripe,
    restaurant: (state) => state.restaurant,
    loading: (state) => state.loading,
    stillLoading: (state) => state.stillLoading,
    validInvoiceInfo: (state) => state.validInvoiceInfo,
    addressChange: (state) => state.addressChange,
    orderErrors: (state) => JSON.parse(JSON.stringify(state.orderErrors)),
    orderDisabled: (state, getters) => !!getters['possibleErrors']?.length,
    calculationErrors: (state) =>
        state.calculationErrors ? JSON.parse(JSON.stringify(state.calculationErrors)) : null,
    sumItems: () => Vue.prototype.$cart?.calculation?.price_sum_items || null,
    minimumOrder: (state) => {
        return state.restaurant.location_info && state.restaurant.location_info.minimum_order
            ? state.restaurant.location_info.minimum_order
            : null
    },
    possibleErrors: (state, getters, rootState, rootGetters) => {
        if (rootGetters['site/appDebug']) debugErrors(getters, rootGetters)
        return [
            // 1.
            !!state.calculationErrors?.length,
            // 2.
            !Vue.prototype.$cart.isGroupOrder &&
            !Vue.prototype.$cart.selected_expense_rule &&
            !Vue.prototype.$cart.cloudcanteen_id &&
            !Vue.prototype.$cart.payment_method
                ? 'payment_method'
                : false,
            // 3.
            Vue.prototype.$cart.isGroupOrder &&
            Vue.prototype.$cart.calculation.price_sum_user > 0 &&
            !Vue.prototype.$cart.payment_method
                ? 'payment_method'
                : false,
            // 4.
            !(
                (Vue.prototype.$cart.isGroupOrder && Vue.prototype.$cart.isGroupSaved) ||
                !Vue.prototype.$cart.isGroupOrder
            )
                ? 'group_info'
                : false,
            // 5.
            !Vue.prototype.$user.isLoggedIn ? 'authentication' : false,
            // 6.
            !(Vue.prototype.$cart.phone && Vue.prototype.$cart.phone_is_valid)
                ? 'phone_number'
                : false,
            // 7.
            !getters['sumItems'] ? 'no_items' : false,
            // 8.
            getters['minimumOrder'] > getters['sumItems'] ? 'minimum_value' : false,
            // 9.
            !getters['validInvoiceInfo'] ? 'invoice_info' : false,
            // 10.
            getters['loading'] || Vue.prototype.$cart.loading,
            // 11.
            Vue.prototype.$cart.delay,
            // 12.
            !Vue.prototype.$cart.selected_expense_rule &&
            !Vue.prototype.$cart.isCloudCanteen &&
            Vue.prototype.$cart.payment_method === Vue.prototype.$cardPayment &&
            !state.stripe.selectedCard &&
            !state.stripe.selectedOnlinePayment
                ? 'select_card'
                : false,
            // 13.
            !Vue.prototype.$cart.hasAddress
                ? Vue.prototype.$user.isLoggedIn && !Vue.prototype.$cart.address
                    ? 'unsaved_address'
                    : 'missing_address'
                : false,
            // 14.
            (Vue.prototype.$cart.hasExpenseRule || Vue.prototype.$cart.isCloudCanteen) &&
            Vue.prototype.$cart.calculation.price_sum_user > 0 &&
            (!Vue.prototype.$cart.payment_method ||
                (Vue.prototype.$cart.payment_method === Vue.prototype.$cardPayment &&
                    !state.stripe.selectedCard &&
                    !state.stripe.selectedOnlinePayment))
                ? 'complementary_payment_method'
                : false,
            // 15.
            getters['addressChange'] ? 'address_change_open' : false,
            Vue.prototype.$cart.hasExpenseRule && Vue.prototype.$cart.expenseCodeHasEmptyRow
                ? {
                      id: 'expense_code',
                      key: 'expense_code_empty_row',
                  }
                : false,
            // 16.
            Vue.prototype.$cart.hasExpenseRule && !Vue.prototype.$cart.expenseCodeIsSaved
                ? {
                      id: 'expense_code',
                      key: 'expense_code_not_saved',
                  }
                : false,
            // 17.
            Vue.prototype.$cart.hasExpenseRule && !Vue.prototype.$cart.expenseCodeIsValid
                ? {
                      id: 'expense_code',
                      key: 'expense_code_not_valid',
                  }
                : false,
            // 18.
            Vue.prototype.$cart.hasExpenseRule && Vue.prototype.$cart.expenseCodeDistributionError
                ? {
                      id: 'expense_code',
                      error: Vue.prototype.$cart.expenseCodeDistributionError,
                  }
                : false,
            // 19. task #155: the chosen scheduled time is no longer offered (tiered restaurant)
            // only when no calculation error already reports the time (never two time alerts)
            rootGetters.tierTimeState === TIER_TIME_UNAVAILABLE &&
            !(state.calculationErrors || []).some((e) => e?.propertyPath === 'requiredDeliveryTime')
                ? {
                      id: 'requiredDeliveryTime',
                      error: Vue.i18n.translate('cart.time.wrong'),
                  }
                : false,
            // 20. task #155: the slots are being refreshed for the new subtotal (silent, like 10.)
            rootGetters.tierTimeState === TIER_TIME_OUTDATED,
        ].filter((error) => error)
    },
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
}

function debugErrors(getters, rootGetters) {
    // eslint-disable-next-line no-console
    console.debug(
        `\x1b[34m = = = possibleErrors = = =` +
            `\n\x1b[34m01. \x1b[33m${(!!getters['calculationErrors']?.length)
                .toString()
                .toUpperCase()}\x1b[37m - There is calculation error` +
            `\n\x1b[34m\x1b[34m02. \x1b[33m${(!!(
                !Vue.prototype.$cart.isGroupOrder &&
                !Vue.prototype.$cart.selected_expense_rule &&
                !Vue.prototype.$cart.cloudcanteen_id &&
                !Vue.prototype.$cart.payment_method
            ))
                .toString()
                .toUpperCase()}\x1b[37m - This order is not a group order and does not have a selected expense rule or payment method, and is not a Cloud Canteen order.` +
            `\n\x1b[34m\x1b[34m03. \x1b[33m${(!!(
                Vue.prototype.$cart.isGroupOrder &&
                Vue.prototype.$cart.calculation.price_sum_user > 0 &&
                !Vue.prototype.$cart.payment_method
            ))
                .toString()
                .toUpperCase()}\x1b[37m - The order is group order and the user needs to pay, but there is no payment method` +
            `\n\x1b[34m04. \x1b[33m${(!(
                (Vue.prototype.$cart.isGroupOrder && Vue.prototype.$cart.isGroupSaved) ||
                !Vue.prototype.$cart.isGroupOrder
            ))
                .toString()
                .toUpperCase()}\x1b[37m - The order is group order, but the group is missing` +
            `\n\x1b[34m05. \x1b[33m${(!Vue.prototype.$user.isLoggedIn)
                .toString()
                .toUpperCase()}\x1b[37m - The user is not logged in` +
            `\n\x1b[34m06. \x1b[33m${(!(
                Vue.prototype.$cart.phone && Vue.prototype.$cart.phone_is_valid
            ))
                .toString()
                .toUpperCase()}\x1b[37m - The given phone number is invalid or missing` +
            `\n\x1b[34m07. \x1b[33m${(!getters['sumItems'])
                .toString()
                .toUpperCase()}\x1b[37m - There are no items in the cart` +
            `\n\x1b[34m08. \x1b[33m${(getters['minimumOrder'] > getters['sumItems'])
                .toString()
                .toUpperCase()}\x1b[37m - The order value is lower than the minimum order value` +
            `\n\x1b[34m09. \x1b[33m${(!getters['validInvoiceInfo'])
                .toString()
                .toUpperCase()}\x1b[37m - Invalid invoice information` +
            `\n\x1b[34m10. \x1b[33m${(!!(getters['loading'] || Vue.prototype.$cart.loading))
                .toString()
                .toUpperCase()}\x1b[37m - Loading` +
            `\n\x1b[34m11. \x1b[33m${(!!Vue.prototype.$cart.delay)
                .toString()
                .toUpperCase()}\x1b[37m - Takes longer than usual for the API to respond` +
            `\n\x1b[34m12. \x1b[33m${(
                !Vue.prototype.$cart.selected_expense_rule &&
                !Vue.prototype.$cart.isCloudCanteen &&
                Vue.prototype.$cart.payment_method === Vue.prototype.$cardPayment &&
                !state.stripe.selectedCard &&
                !state.stripe.selectedOnlinePayment
            )
                .toString()
                .toUpperCase()}\x1b[37m - The paying method is stripe, but no card selected` +
            `\n\x1b[34m13. \x1b[33m${(!Vue.prototype.$cart.hasAddress)
                .toString()
                .toUpperCase()}\x1b[37m - The address is missing` +
            `\n\x1b[34m14. \x1b[33m${(!!(
                (Vue.prototype.$cart.selected_expense_rule || Vue.prototype.$cart.isCloudCanteen) &&
                Vue.prototype.$cart.calculation.price_sum_user > 0 &&
                (!Vue.prototype.$cart.payment_method ||
                    (Vue.prototype.$cart.payment_method === Vue.prototype.$cardPayment &&
                        !state.stripe.selectedCard &&
                        !state.stripe.selectedOnlinePayment))
            ))
                .toString()
                .toUpperCase()}\x1b[37m - With expense rule or cloud canteen, the user needs to pay, but there is no payment method` +
            `\n\x1b[34m15. \x1b[33m${(
                Vue.prototype.$cart.hasExpenseRule && Vue.prototype.$cart.expenseCodeHasEmptyRow
            )
                .toString()
                .toUpperCase()}\x1b[37m - There is a selected expense rule, but the expense code block has an empty row` +
            `\n\x1b[34m16. \x1b[33m${(
                Vue.prototype.$cart.hasExpenseRule && !Vue.prototype.$cart.expenseCodeIsSaved
            )
                .toString()
                .toUpperCase()}\x1b[37m - There is a selected expense rule, but the expense code is not saved` +
            `\n\x1b[34m17. \x1b[33m${(
                Vue.prototype.$cart.hasExpenseRule && !Vue.prototype.$cart.expenseCodeIsValid
            )
                .toString()
                .toUpperCase()}\x1b[37m - There is a selected expense rule, but the expense code is invalid` +
            `\n\x1b[34m18. \x1b[33m${(
                Vue.prototype.$cart.hasExpenseRule &&
                !!Vue.prototype.$cart.expenseCodeDistributionError
            )
                .toString()
                .toUpperCase()}\x1b[37m - There is a selected expense rule, but the distribution has error` +
            `\n\x1b[34m19. \x1b[33m${(rootGetters.tierTimeState === TIER_TIME_UNAVAILABLE)
                .toString()
                .toUpperCase()}\x1b[37m - The selected scheduled time is not offered for the subtotal` +
            `\n\x1b[34m20. \x1b[33m${(rootGetters.tierTimeState === TIER_TIME_OUTDATED)
                .toString()
                .toUpperCase()}\x1b[37m - The delivery periods are being refreshed for the subtotal` +
            `\n\n\x1b[34m = = = = = = = = = = = =`
    )
}
